import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaSave, 
  FaTrash, 
  FaPlay, 
  FaClock, 
  FaDatabase, 
  FaFileArchive,
  FaCalendar,
  FaPlus,
  FaHistory,
  FaCog,
  FaDownload,
  FaEye
} from 'react-icons/fa';
import '../css/BackupManager.css';
import config from '../config/config';

// Configure a URL base do axios (ajuste conforme sua configuração)

const BackupManager = () => {
  const [crons, setCrons] = useState([]);
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCron, setShowAddCron] = useState(false);
  const [showExecuteBackup, setShowExecuteBackup] = useState(false);
  const [newCron, setNewCron] = useState({
    schedule: '0 2 * * *',
    description: '',
    type: 'FULL'
  });

  const { API_BASE_URL } = config;

  const fetchBackupData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      
      const [cronsResponse, backupsResponse, statusResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/backup/crons`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        axios.get(`${API_BASE_URL}/api/backup/list`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        axios.get(`${API_BASE_URL}/api/backup/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      // Com axios, os dados estão em response.data.data
      if (cronsResponse.data.success) {
        setCrons(cronsResponse.data.data || []);
      }

      if (backupsResponse.data.success) {
        setBackups(backupsResponse.data.data || []);
      }

      if (statusResponse.data.success) {
        setStatus(statusResponse.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de backup:', error);
      
      // Fallback com dados mock em caso de erro
      if (error.response) {
        console.error('Erro da API:', error.response.data);
      }

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackupData();
    const interval = setInterval(fetchBackupData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCreateCron = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await axios.post(`${API_BASE_URL}/api/backup/crons`, newCron, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setShowAddCron(false);
        setNewCron({ schedule: '0 2 * * *', description: '', type: 'FULL' });
        fetchBackupData();
        alert('CRON adicionado com sucesso!');
      } else {
        alert('Erro ao criar CRON: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro:', error);
      if (error.response) {
        alert('Erro ao criar CRON: ' + (error.response.data.message || error.message));
      } else {
        alert('Erro ao criar CRON: ' + error.message);
      }
    }
  };

  const handleDeleteCron = async (cronId) => {
    if (window.confirm('Tem certeza que deseja remover este agendamento?')) {
      try {
        const token = localStorage.getItem('jwtToken');
        // Extrair índice do ID (cron_0 -> 0)
        const index = parseInt(cronId.split('_')[1]);
        
        const response = await axios.delete(`${API_BASE_URL}/api/backup/crons/${index}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          fetchBackupData();
          alert('Agendamento removido com sucesso!');
        } else {
          alert('Erro ao remover agendamento: ' + (response.data.message || 'Erro desconhecido'));
        }
      } catch (error) {
        console.error('Erro:', error);
        if (error.response) {
          alert('Erro ao remover agendamento: ' + (error.response.data.message || error.message));
        } else {
          alert('Erro ao remover agendamento: ' + error.message);
        }
      }
    }
  };

  const handleExecuteBackup = async () => {
    const type = document.getElementById('backupType')?.value || 'FULL';
    const description = document.getElementById('backupDescription')?.value || 
      `Backup manual - ${new Date().toLocaleString('pt-BR')}`;

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await axios.post(`${API_BASE_URL}/api/backup/execute`, 
        { type, description }, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setShowExecuteBackup(false);
        alert('Backup executado com sucesso!');
        fetchBackupData();
      } else {
        alert('Erro ao executar backup: ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro:', error);
      if (error.response) {
        alert('Erro ao executar backup: ' + (error.response.data.message || error.message));
      } else {
        alert('Erro ao executar backup: ' + error.message);
      }
    }
  };

  const getCronTypeIcon = (type) => {
    switch (type) {
      case 'DATABASE': return <FaDatabase />;
      case 'FILES': return <FaFileArchive />;
      default: return <FaSave />;
    }
  };

  const getCronTypeColor = (type) => {
    switch (type) {
      case 'DATABASE': return '#e74c3c';
      case 'FILES': return '#3498db';
      default: return '#27ae60';
    }
  };

  const getCronTypeLabel = (type) => {
    switch (type) {
      case 'DATABASE': return 'Banco de Dados';
      case 'FILES': return 'Arquivos';
      default: return 'Completo';
    }
  };

  const handleDownloadBackup = (backupName) => {
    // Simular download - em produção seria um link real
    alert(`Iniciando download: ${backupName}`);
  };

  const handleViewBackup = (backupName) => {
    // Simular visualização - em produção abriria detalhes
    alert(`Visualizando: ${backupName}`);
  };


  if (loading) {
    return (
      <div className="backup-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Carregando informações de backup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backup-manager">
      {/* Header */}
      <div className="backup-header">
        <h1>
          <FaSave />
          Gerenciamento de Backups
        </h1>
        <div className="header-actions">
          <button 
            className="btn btn-execute"
            onClick={() => setShowExecuteBackup(true)}
          >
            <FaPlay /> Executar Backup
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddCron(true)}
          >
            <FaPlus /> Novo Agendamento
          </button>
        </div>
      </div>

      {/* Status Overview */}
      {status && (
        <div className="status-overview">
          <div className="status-card">
            <div className="status-icon total">
              <FaSave />
            </div>
            <div className="status-info">
              <h3>{status.totalBackups}</h3>
              <p>Total de Backups</p>
            </div>
          </div>
          <div className="status-card">
            <div className="status-icon size">
              <FaDatabase />
            </div>
            <div className="status-info">
              <h3>{formatFileSize(status.totalSize)}</h3>
              <p>Espaço Utilizado</p>
            </div>
          </div>
          <div className="status-card">
            <div className="status-icon crons">
              <FaClock />
            </div>
            <div className="status-info">
              <h3>{status.activeCrons}</h3>
              <p>Agendamentos Ativos</p>
            </div>
          </div>
          <div className="status-card">
            <div className="status-icon last">
              <FaHistory />
            </div>
            <div className="status-info">
              <h3>{status.lastBackup}</h3>
              <p>Último Backup</p>
            </div>
          </div>
        </div>
      )}

      {/* CRONs Section */}
      <div className="section">
        <div className="section-header">
          <h2>
            <FaClock />
            Agendamentos de Backup
          </h2>
          <span className="section-badge">{crons.length} agendamentos</span>
        </div>
        
        <div className="crons-grid">
          {crons.map((cron, index) => (
            <div key={cron.id} className="cron-card">
              <div className="cron-header">
                <div 
                  className="cron-type-icon"
                  style={{ backgroundColor: getCronTypeColor(cron.type) }}
                >
                  {getCronTypeIcon(cron.type)}
                </div>
                <div className="cron-info">
                  <h4>{cron.description}</h4>
                  <span className="cron-type-badge">
                    {getCronTypeLabel(cron.type)}
                  </span>
                </div>
              </div>
              
              <div className="cron-schedule">
                <FaCalendar />
                <span>{cron.schedule}</span>
              </div>
              
              <div className="cron-details">
                <div className="cron-detail">
                  <span className="label">Status:</span>
                  <span className="status-badge active">Ativo</span>
                </div>
                <div className="cron-detail">
                  <span className="label">Última Execução:</span>
                  <span className="value">{formatDate(cron.lastExecution)}</span>
                </div>
              </div>
              
              <div className="cron-actions">
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteCron(cron.id)}
                  title="Remover agendamento"
                >
                  <FaTrash /> Remover
                </button>
              </div>
            </div>
          ))}
          
          {crons.length === 0 && (
            <div className="empty-state">
              <FaCog size={48} />
              <h3>Nenhum agendamento configurado</h3>
              <p>Adicione agendamentos para automatizar seus backups</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddCron(true)}
              >
                <FaPlus /> Criar Primeiro Agendamento
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Backups List */}
      <div className="section">
        <div className="section-header">
          <h2>
            <FaFileArchive />
            Backups Existentes
          </h2>
          <span className="section-badge">{backups.length} arquivos</span>
        </div>
        
        <div className="backups-grid">
          {backups.map((backup, index) => (
            <div key={index} className="backup-card">
              <div className="backup-header">
                <div className="backup-icon">
                  <FaFileArchive />
                </div>
                <div className="backup-info">
                  <h4>{backup.name}</h4>
                  <span className="backup-size">{backup.readableSize}</span>
                </div>
              </div>
              
              <div className="backup-details">
                <div className="backup-detail">
                  <span className="label">Criado em:</span>
                  <span className="value">{backup.formattedDate}</span>
                </div>
              </div>
              
              <div className="backup-actions">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleViewBackup(backup.name)}
                  title="Visualizar detalhes"
                >
                  <FaEye /> Detalhes
                </button>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => handleDownloadBackup(backup.name)}
                  title="Download"
                >
                  <FaDownload /> Download
                </button>
              </div>
            </div>
          ))}
          
          {backups.length === 0 && (
            <div className="empty-state">
              <FaFileArchive size={48} />
              <h3>Nenhum backup encontrado</h3>
              <p>Execute seu primeiro backup para começar</p>
              <button 
                className="btn btn-execute"
                onClick={() => setShowExecuteBackup(true)}
              >
                <FaPlay /> Executar Primeiro Backup
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddCron && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>➕ Novo Agendamento</h3>
            
            <div className="form-group">
              <label>Descrição:</label>
              <input
                type="text"
                value={newCron.description}
                onChange={(e) => setNewCron({...newCron, description: e.target.value})}
                placeholder="Ex: Backup diário completo"
              />
            </div>
            
            <div className="form-group">
              <label>Tipo de Backup:</label>
              <select
                value={newCron.type}
                onChange={(e) => setNewCron({...newCron, type: e.target.value})}
              >
                <option value="FULL">Completo</option>
                <option value="DATABASE">Apenas Banco</option>
                <option value="FILES">Apenas Arquivos</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Agendamento (CRON):</label>
              <input
                type="text"
                value={newCron.schedule}
                onChange={(e) => setNewCron({...newCron, schedule: e.target.value})}
                placeholder="0 2 * * *"
              />
              <small>
                <strong>Exemplos:</strong><br/>
                • 0 2 * * * → Diário às 2h<br/>
                • 0 0 * * 0 → Semanal aos domingos<br/>
                • */30 * * * * → A cada 30 minutos
              </small>
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-cancel" onClick={() => setShowAddCron(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleCreateCron}>
                <FaPlus /> Criar Agendamento
              </button>
            </div>
          </div>
        </div>
      )}

      {showExecuteBackup && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>🚀 Executar Backup Agora</h3>
            
            <div className="form-group">
              <label>Tipo de Backup:</label>
              <select id="backupType" defaultValue="FULL">
                <option value="FULL">Completo</option>
                <option value="DATABASE">Apenas Banco</option>
                <option value="FILES">Apenas Arquivos</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Descrição (opcional):</label>
              <input
                type="text"
                id="backupDescription"
                placeholder={`Backup manual - ${new Date().toLocaleString('pt-BR')}`}
              />
            </div>
            
            <div className="modal-actions">
              <button className="btn btn-cancel" onClick={() => setShowExecuteBackup(false)}>
                Cancelar
              </button>
              <button className="btn btn-execute" onClick={handleExecuteBackup}>
                <FaPlay /> Executar Backup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Funções auxiliares
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return 'Nunca';
  try {
    return new Date(dateString).toLocaleString('pt-BR');
  } catch {
    return dateString;
  }
};

export default BackupManager;